import { useEffect } from 'react';

import { clearMastraTokenCache } from '@/hooks/mastra/use-mastra-client';
import { getCable } from '@/lib/cable';
import type { CreditNotice, CreditStatus } from '@/types/shared';

export function useCreditSettlementEvents({
  threadId,
  onCreditStatus,
  onNotices,
}: {
  threadId?: string;
  onCreditStatus?: (status: CreditStatus) => void;
  onNotices?: (notices: CreditNotice[]) => void | Promise<void>;
}) {
  useEffect(() => {
    const subscription = getCable().subscriptions.create(
      { channel: 'AiCreditStatusChannel' },
      {
        received: (event: {
          type?: string;
          threadId?: string;
          creditStatus?: CreditStatus;
          notices?: CreditNotice[];
        }) => {
          if (event.type !== 'credit_purchased' && event.type !== 'credit_settled') return;
          if (event.creditStatus) onCreditStatus?.(event.creditStatus);

          // Discard any cached JWT the moment the balance changes in either direction -
          // on a purchase, so the next token reflects the new balance instead of riding
          // out the cache; on crossing into exhausted, so the next token is stamped
          // 'exhausted' instead of the stale pre-crossing value.
          if (
            event.type === 'credit_purchased' ||
            (event.type === 'credit_settled' && event.creditStatus?.level === 'exhausted')
          ) {
            clearMastraTokenCache();
          }

          const isOwnThreadSettlement =
            event.type === 'credit_settled' && event.threadId === threadId;
          if (isOwnThreadSettlement && event.notices?.length && onNotices)
            void onNotices(event.notices);
        },
      },
    );

    return () => subscription.unsubscribe();
  }, [onCreditStatus, onNotices, threadId]);
}
